import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, from, map, of, switchMap, take, withLatestFrom } from 'rxjs';
import { Store } from '@ngrx/store';

import { DataRepository } from '@core/data/data-repository';
import { withActivityLog } from '@core/util/project-activity';
import { selectCurrentPersona } from '@state/session/session.reducer';
import { ProjectsApiActions, ProjectsPageActions } from './projects.actions';
import { selectAllProjects } from './projects.reducer';

@Injectable()
export class ProjectsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly repo = inject(DataRepository);

  /** On page entry, load all projects from the repository (latest wins). */
  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsPageActions.entered),
      switchMap(() =>
        from(this.repo.listProjects()).pipe(
          map((projects) => ProjectsApiActions.loadSuccess({ projects })),
          catchError((err: unknown) =>
            of(ProjectsApiActions.loadFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /**
   * Persist the whole project set on any single-project save. The repository
   * contract is collection-based today; a future API repository would PATCH the
   * one project instead, but the effect boundary stays identical.
   */
  readonly save$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsPageActions.projectSaved),
      // concatMap (not switchMap) so several saves dispatched in one tick — e.g.
      // bulk-approving a day's time entries that span multiple projects — each run
      // to completion instead of the later ones cancelling the earlier. The latest
      // projects list is read INSIDE, per save, so each builds on the prior result.
      concatMap(({ project }) =>
        this.store.select(selectAllProjects).pipe(
          take(1),
          withLatestFrom(this.store.select(selectCurrentPersona)),
          switchMap(([all, actor]) => {
            const prev = all.find((p) => p.id === project.id) ?? null;
            // Log what changed to the project's History (edit) trail before persisting.
            const enriched = prev ? withActivityLog(prev, project, actor) : project;
            const next = prev
              ? all.map((p) => (p.id === enriched.id ? enriched : p))
              : [...all, enriched];
            return from(this.repo.saveProjects(next)).pipe(
              map(() => ProjectsApiActions.saveSuccess({ project: enriched })),
              catchError((err: unknown) =>
                of(ProjectsApiActions.saveFailure({ error: toMessage(err) })),
              ),
            );
          }),
        ),
      ),
    ),
  );
}

function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}
